import { LitElement, html, css, TemplateResult } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { CoordinatesUtils } from '../../../src/utils/coordinates-utils';
import { getVisibilityValueTexts, getIcingValueTexts } from '../../../src/utils/weather-limits';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';

export interface WarningEntry
{
    type: string;
    actual?: number;
    limit: number;
}

@customElement('voyage-weather-warning')
export class VoyageWeatherWarning extends LitElement
{
    @property({type: Array}) coordinates: [number, number] = [0, 0];
    @property({type: String}) timestamp = "";
    @property({type: Array}) warnings: WarningEntry[] = [];
    @property({type: Array}) avoids: WarningEntry[] = [];
    @property({type: Boolean}) parametricRoll: boolean = false;
    @property({type: Number}) imo: number|undefined;

    static get styles()
    {
        return [
            css`
            :host {
                position: absolute;
                margin: 0;
                padding: 0;
            }

            .container {
                display: flex;
                flex-direction: column;
                align-items: stretch;
                font-family: MaerskText;
                background-color: var(--neutrals-grey-10, white);
            }

            .avoidHeader {
                font-weight: bold;
                font-size: 12px;
                color: var(--neutrals-grey-10, white);
                background-color: #ff6000;
                text-align: center;
            }

            .warningHeader {
                font-weight: lighter;
                font-size: 12px;
                color: var(--neutrals-grey-1, black);
                background-color: var(--warning-yellow-7, yellow);
                text-align: center;
            }

            .rollHeader {
                font-weight: bold;
                font-size: 12px;
                color: var(--neutrals-grey-10, white);
                background-color: var(--voyage-parametric-roll-color, #E9B2B7);
                text-align: center;
            }
            
            .info {
                display: flex;
                border: 1px solid var(--neutrals-grey-07, grey);
            }

            .position {
                box-sizing: border-box;
                flex-grow: 1;
                border-right: 1px solid var(--neutrals-grey-07, grey);
                padding: 10px;
                padding-top: 15px;
                padding-bottom: 15px;
            }

            .eta {
                padding: 10px;
                padding-top: 15px;
                padding-bottom: 15px;
            }

            .innerHeader {
                font-weight: lighter;
                font-size: 12px;
                color: var(--neutrals-grey-04, grey);
            }

            .standardText {
                color: var(--neutrals-grey-02, black);
                font-size: 14px;
            }

            .warnings {
                border: 1px solid var(--neutrals-grey-07, grey);
                border-top: none;
                display: grid;
                grid-template-columns: auto auto;
                grid-gap: 10px;
                padding: 10px;
                padding-top: 15px;
                padding-bottom: 15px;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
            }

            .avoidActual {
                color: var(--voyage-error-red, red);
            }

            .warningActual {
                color: var(--warning-yellow-7, yellow);
            }

            .roll-warning-container {
                
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 10px 0 10px 0;
            }

            .warning-icon {
                color: var(--voyage-error-red);
                margin-bottom: 10px
            }

            .warning-icon > svg {
                width: 41px; 
                height: 37px;
            }
            `
        ];
    }

    render()
    {
        const filteredWarnings = this._removeAvoidDuplicates(this.warnings);
        
        if (this.warnings.length === 0 && this.avoids.length === 0 && !this.parametricRoll){
            return html``;
        }

        return html`
        <div class="container">
            ${this.avoids.length > 0 ? html`
            <div class="container">
                <div class="avoidHeader">COMPANY LIMITS</div>
                ${this._renderInfo()}
                ${this._renderWeatherWarnings(this.avoids, true)}
            </div>
            `:html``}

            <!-- Hiding warning  ${filteredWarnings.length > 0 ? html`
            <div class="container">
                <div class="warningHeader">WARNING</div>
                ${this.avoids.length === 0 ? this._renderInfo() : html`` }
                ${this._renderWeatherWarnings(filteredWarnings, false)}
            </div>
            `:html``} -->

            ${this.parametricRoll ? html`
            <div class="container">
                <div class="rollHeader">RISK OF PARAMETRIC ROLL</div>
                ${this._renderInfo()}
                <div class="roll-warning-container">
                    <div class="warning-icon">${SvgIcons.exclamationTriangle}</div>
                    <div class="standardText">Risk of Parametric Roll</div>
                </div>
            </div>
            </div>
            `:html``}
        </div>
        `;
    }

    private _renderInfo()
    {
        return html`
        <div class="info">
            <div class="position">
                <div class="innerHeader">COORDINATES</div>
                <div class="standardText">${this._getNavalCoordinates()}</div>
            </div>
            <div class="eta">
                <div class="innerHeader">ETA</div>
                <div class="standardText">${this._getETA()}</div>
            </div>
        </div>
        `;
    }

    private _renderWeatherWarnings(warnings: WarningEntry[], isAvoid: boolean): TemplateResult
    {
        return html`
        <div class="warnings">
            <div class="innerHeader">WEATHER LIMITS</div>
            <div class="innerHeader">FORECAST / LIMIT</div>
            ${warnings.concat().sort( (a,b) => a.type.localeCompare(b.type) ).map( warning => html`
            <div>${this._getLimitDescription(warning.type)}</div>
            <div>
                <span class=${isAvoid ? "avoidActual" : "warningActual"}>${this._getActual(warning)}</span>
                ${` / ${this._getLimit(warning)} ${this._getUnit(warning.type)}`}
            </div>
            `)}
        </div>
        `;
    }

    private _getNavalCoordinates(): string
    {
        const lonLat = CoordinatesUtils.decimalToDegreesAndMinutes(this.coordinates);
        return `${lonLat.lat}, ${lonLat.lon}`;
    }

    private _getETA(): string
    {
        const delta = new Date(this.timestamp).getTime() - Date.now();
        if (delta <= 0)
        {
            // Already past this point
            return "";
        }

        const minutes = delta/(60*1000);
        const hours = minutes/60;
        if (hours < 1)
        {
            return `${Math.floor(minutes)} MINUTES`;
        }
        return `${Math.floor(hours)} HOURS`;
    }

    private _getLimitDescription(type: string): string
    {
        switch (type)
        {
            case "Hurricanes": return "Min. distance to hurricanes";
            case "TropicalStorms": return "Min. distance to tropical storms";
            case "Depressions": return "Min. distance to tropical depressions";
            case "Wind10M": return "Max. wind speed 10m";
            case "Wind50M": return "Max. wind speed 50m";
            case "WindGusts10M": return "Max. wind gusts 10m";
            case "WindGusts50M": return "Max. wind gusts 50m";
            case "RiskWindSpeed": return "Max. risk wind speed";
            case "Waves": return "Max. wave height";
            case "RiskWaveHeight": return "Max. risk wave height";
            case "HeadWavesHeight": return "Max. head waves height";
            case "BeamWavesHeight": return "Max. beam waves height";
            case "FollowingWavesHeight": return "Max. following waves height";
            case "Visibility": return "Min. visibility";
            case "Icing": return "Max. icing";
            default:
                console.warn(`${type} not recognized as a weather limit!`);
                return type;
        }
    }

    private _getActual(warning: WarningEntry): string
    {
        if (warning.actual === undefined)
        {
            // Some warnings have no actual value (like distances to e.g. Hurricanes)
            return "-";
        }

        switch (warning.type)
        {
            case "Visibility":
                return getVisibilityValueTexts()[warning.actual!];

            case "Icing":
                return getIcingValueTexts()[warning.actual!];

            default:
                // The rest are simple values already in the correct unit
                return warning.actual!.toFixed(1);
        }
    }

    private _getLimit(warning: WarningEntry): string
    {
        switch (warning.type)
        {
            case "Hurricanes":
            case "TropicalStorms":
            case "Depressions":
                // Needs conversion from meters to nautical miles
                return (warning.limit/1852).toFixed(1);

            case "Visibility":
                return getVisibilityValueTexts()[warning.limit];

            case "Icing":
                return getIcingValueTexts()[warning.limit];

            default:
                // The rest are simple values already in the correct unit
                return warning.limit.toFixed(1);
        }
    }

    private _getUnit(type: string): string
    {
        switch (type)
        {
            case "Hurricanes":
            case "TropicalStorms":
            case "Depressions":
                return "nm";

            case "Wind10M":
            case "Wind50M":
            case "WindGusts10M":
            case "WindGusts50M":
            case "RiskWindSpeed":
                return "m/s";

            case "Waves":
            case "RiskWaveHeight":
            case "HeadWavesHeight":
            case "BeamWavesHeight":
            case "FollowingWavesHeight":
                return "m";

            case "Visibility":
            case "Icing":
                return "";
            default:
                console.warn(`${type} not recognized as a weather limit!`);
                return "";
        }
    }

    private _removeAvoidDuplicates(warnings: WarningEntry[]): WarningEntry[]
    {
        if (warnings.length > 0 && this.avoids.length > 0)
        {
            return this.warnings.filter(warning => {
                return !this.avoids.find(avoid => avoid.type === warning.type)
            })
        } else
        {
            return warnings;
        }
    }
};
